import * as Sentry from '@sentry/react'
import _ from 'lodash'
import { action, computed, observable, reaction } from 'mobx'
import { createContext } from 'react'

import { formatUserName } from '@/utils/userHelpers'

import { BaseStore } from './BaseStore'

const INITIAL_STATE = {
  accessToken: '',
  refreshToken: '',
  user: {},
  userId: '',
}

const TYPES = {
  accessToken: observable,
  refreshToken: observable,
  user: observable,
  userId: observable,
  isAuthenticated: computed,
  needsProfileCompletion: computed,
  needsRecoveryMethod: computed,
}

/**
 * Returns the entity IDs for the user.
 *
 * @param {*} user
 * @returns {string[]}
 */
const getEntityIds = (user) => {
  const roles = user?.entityRoles ?? []
  return roles.map((r) => r?.entity?.id).filter(Boolean)
}

/**
 * Returns the first entity ID for the user.
 *
 * @param {*} user
 * @returns {string|null}
 */
const getFirstEntityId = (user) => {
  const ids = getEntityIds(user)
  return _.head(ids) ?? null
}

/**
 * Returns whether the user has an active entity.
 *
 * @param {*} user
 * @returns {boolean}
 */
const hasActiveEntity = (user) => {
  const activeEntity = user?.activeEntity ?? null
  return !!activeEntity
}

/**
 * Returns whether the user's active entity is in their entity roles.
 *
 * @param {*} user
 * @returns {boolean}
 */
const activeEntityInRoles = (user) => {
  const activeEntity = user?.activeEntity ?? null
  return activeEntity ? _.includes(getEntityIds(user), activeEntity) : false
}

export class UserStore extends BaseStore {
  get isAuthenticated() {
    return this.accessToken !== '' && this.user?.id !== null && this.user?.id !== undefined
  }

  get needsProfileCompletion() {
    if (!this.user?.id) return false
    return !this.user.firstName?.trim() || !this.user.lastName?.trim()
  }

  /**
   * Whether the user still owes a second way back into their account — a
   * verified additional email or a phone number (RSCX-377). A primary address
   * is usually employer-owned, so an account reachable only through it is lost
   * when the job ends. The API decides; the store only reflects the flag, and
   * treats a response that predates it as "nothing owed".
   */
  get needsRecoveryMethod() {
    if (!this.user?.id) return false
    return this.user.recoveryMethodRequired === true
  }

  get activeEntity() {
    if (!this.user) return null
    return this.user.activeEntity
  }

  get isXverseAdmin() {
    if (!this.user) return false
    return this.user.isXverseAdmin
  }

  get isXverseInstructor() {
    if (!this.user) return false
    return this.user.isXverseInstructor === true
  }

  /**
   * Returns the role for the active entity, or null if the user doesn't have a
   * role for the active entity.
   */
  getRoleForActiveEntity(roleType) {
    return (
      _.find(
        this.user?.entityRoles ?? [],
        (r) => r?.entity?.id === this.user?.activeEntity && r?.roles?.includes(roleType),
      ) || null
    )
  }

  get memberRoleForActiveEntity() {
    return this.getRoleForActiveEntity('Member')
  }

  get isEntityOwner() {
    return !!this.getRoleForActiveEntity('Owner')
  }

  get isEntityAdmin() {
    return !!this.getRoleForActiveEntity('Admin')
  }

  get isEntityInstructor() {
    return !!this.getRoleForActiveEntity('Instructor')
  }

  /**
   * Xverse Instructor Candidate — platform-level flag parallel to isXverseAdmin /
   * isXverseInstructor. Grants read-only access to instructor-side resources in any
   * entity the user is a member of, without permitting manager actions.
   */
  get isXverseInstructorCandidate() {
    if (!this.user) return false
    return this.user.isXverseInstructorCandidate === true
  }

  /** Helper to check if the user has any non-student role in the active entity, or is an XVerse admin */
  get canManageEntity() {
    return (
      this.isEntityAdmin || this.isEntityInstructor || this.isEntityOwner || this.isXverseAdmin
    )
  }

  // ----------------------------- Organizations -----------------------------
  // Optional parent-account tier (RSCX-320). Surfaced from the API as
  // `organizationRoles` (denormalized: { organizationId, organizationName, roles })
  // plus `activeOrganization`. Org Owners/Admins get full admin into every child
  // facility; the backend enforces this, the frontend just reflects context.

  get activeOrganization() {
    if (!this.user) return null
    return this.user.activeOrganization ?? null
  }

  get organizationRoles() {
    return this.user?.organizationRoles ?? []
  }

  /** Role entry for the active organization, if the user holds the given role. */
  getRoleForActiveOrganization(roleType) {
    return (
      _.find(
        this.organizationRoles,
        (r) => r?.organizationId === this.activeOrganization && r?.roles?.includes(roleType),
      ) || null
    )
  }

  get isOrganizationOwner() {
    return !!this.getRoleForActiveOrganization('Owner')
  }

  get isOrganizationAdmin() {
    return !!this.getRoleForActiveOrganization('Admin')
  }

  /** Owner/Admin of the active organization, or an Xverse admin. */
  get canManageOrganization() {
    return this.isOrganizationOwner || this.isOrganizationAdmin || this.isXverseAdmin
  }

  setActiveOrganization = action((id) => {
    const next = id ?? null
    const current = this.user?.activeOrganization ?? null
    if (current === next) return
    this.user = { ...this.user, activeOrganization: next }
  })

  /**
   * Whether the user can perform manager actions on instructor resources for the active entity
   * (e.g. assigning students to courses, issuing/removing student certificates).
   * Xverse Instructor Candidates have read-only access and are intentionally excluded here.
   */
  get canManageInstructorResources() {
    return this.canManageEntity
  }

  updateEntityInRoles = action((updatedEntity) => {
    if (!this.user?.entityRoles || !updatedEntity?.id) return
    this.user = {
      ...this.user,
      entityRoles: this.user.entityRoles.map((role) => {
        if (role.entity?.id === updatedEntity.id) {
          return { ...role, entity: { ...role.entity, ...updatedEntity } }
        }
        return role
      }),
    }
  })

  setActiveEntity = action((id) => {
    const next = id ?? null
    const current = this.user?.activeEntity ?? null
    if (current === next) return
    this.user = { ...this.user, activeEntity: next }
  })

  ensureActiveEntityInvariant = action(() => {
    // When operating in an organization context, an active entity is not
    // required — the org dashboard stands on its own (RSCX-320). Don't force
    // the user back into a facility.
    if (this.user?.activeOrganization) return

    const ids = getEntityIds(this.user)
    if (!ids.length && !this.isXverseAdmin) {
      this.setActiveEntity(null)
      return
    }

    if (!this.isXverseAdmin) {
      if (!hasActiveEntity(this.user) || !activeEntityInRoles(this.user)) {
        this.setActiveEntity(getFirstEntityId(this.user))
      }
    }
  })

  constructor(...args) {
    super(...args)

    /**
     * R1: isXverseAdmin true -> false
     * When isXverseAdmin changes from true to false AND the user has no active entity,
     * automatically set their active entity to the first entity in their roles.
     */
    reaction(
      () => this.isXverseAdmin,
      (isAdmin, prev) => {
        if (prev && !isAdmin && !hasActiveEntity(this.user)) {
          this.setActiveEntity(getFirstEntityId(this.user))
        }
      },
    )

    /**
     * R2: roles change
     * Ensure non-admin users always have a valid active entity that exists in their entity roles.
     */
    reaction(
      () => ({
        // Convert entity IDs array to string so MobX detects when roles change
        rolesKey: getEntityIds(this.user).join(':'),
        active: this.user?.activeEntity ?? null,
        isAdmin: this.isXverseAdmin,
      }),
      () => this.ensureActiveEntityInvariant(),
      // , { fireImmediately: true }
    )
  }

  setTokens = action(({ access, refresh }) => {
    this.accessToken = access
    this.refreshToken = refresh
  })

  setUserId = action((id) => {
    this.userId = id
  })

  setUser = action((user) => {
    this.user = {
      ...this.user,
      ...user,
    }

    this.setSentryUser()

    this.ensureActiveEntityInvariant()
  })

  setSentryUser = () => {
    Sentry.setUser({
      ...this.user,
      username: formatUserName(this.user),
    })
  }
}

export const user = new UserStore(INITIAL_STATE, TYPES)
export const UserStoreContext = createContext(user)
